/*#
# Cached log: a remote log with a local replica

The board reads from a local replica and writes to the host. `CachedLog` is
the `SyncLog` that makes that one object: a **remote** log (the host, through
[`RemoteLog`](/remote/)) and a **cache** (the [IndexedDB log](/indexeddb-log/)
in a browser, a `MemoryLog` anywhere else).

- A **pull** is served from the cache first. When the cache runs out, the
  delta after its tail is pulled from the host, appended to the cache with
  the host's sequence numbers, and returned. So a cold start replays what the
  cache holds without touching the network, then catches up; a warm sync is
  one small request.
- A **push** goes to the host — atomically, idempotent by id — and nothing
  is written to the cache from the push result. The store then replays, and
  the replay pulls the new events back through the cache like anything
  else. The log is the truth; the cache is a copy of it. That is the same
  invariant the local logs keep, now with a network in between.
- The cache is **per host per browser**, and keyed by the credential as
  well as the host — `replicaDbName(host, principal)` (`s:<uid>` for a
  session, `t:<token fingerprint>` for a token), so a different credential
  opens a different database by construction and can never replay what
  another credential was allowed to read; a page reload, a second tab and
  a fresh board included. It never holds anything the host did not
  sequence. Deleting it costs one cold load.
- A replica copied from a host that has since **started over** (a reset, or
  a restart that lost its log) is not a replica of that host any more. So a
  session that opens a warm replica first asks the host for the event at the
  replica's tail. If the host has nothing there, or has a different event,
  the load fails with `HostResetError`. The board then drops the replica and
  loads again from the host, as "Reload from server" does. The CLI deletes
  its replica files, says so on stderr, and loads again. A fresh replica has
  nothing to check, so this happens once. The check runs once per session:
  a host reset in the middle of a session shows up on the next reload.

A pull while the host is unreachable answers from the replica alone — a
degraded, read-only view, announced through `onRemoteError` (the board
shows `offline` over real cards; the CLI answers read verbs) — unless the
replica is empty, in which case there is nothing to show and the pull
fails. A **refused credential** (401/403) is not an outage and is never
absorbed: the pull fails so the caller can forget the token or ask for a
sign-in, instead of serving a replica the reader is no longer allowed to
see. And while the replica is stale a **push is refused**
(`StaleReplicaError`): a writer must not decide what is new against a view
the host has moved past — an importer would resend ids the host holds.
Sync, then retry. There is no offline write queue: a push while the host is
unreachable fails, and the card stays as it was. That is the design's "not designed
here" (DESIGN.md §7.2) and it stays that way until it hurts.

```
const log = new CachedLog({
  remote: new RemoteLog({ client: new ServiceClient({ host, token }) }),
  cache: new IndexedDBLog(replicaDbName(new URL(host).host, principal)),
})
const store = new VirtaStore({ log, identity })
await store.load()          // the cache, then the delta
setInterval(() => store.sync(), 10_000)   // other writers' events arrive here
```
*/

/*{ "parent": "Reference", "order": 98 }*/

import type { EventDraft, VirtaEvent } from './model'
import { DEFAULT_PAGE, type LogPage, type SyncLog } from './log'

/** A local log that can take events with their sequence numbers already assigned. */
export interface ReplicaLog extends SyncLog {
  /** Store events exactly as given (their `seq` is the host's); an id already held is skipped. */
  append(events: VirtaEvent[]): Promise<void>
  /** The highest `seq` held, or 0 when empty. */
  tail(): Promise<number>
}

export interface CachedLogOptions {
  remote: SyncLog
  cache: ReplicaLog
  /**
   * Called when the host cannot be reached during a pull. The pull then
   * answers from the replica alone (a degraded, read-only view) instead of
   * failing — but only if the replica holds something: an empty replica
   * with no host has nothing to show, and that pull fails as before.
   */
  onRemoteError?: (error: unknown) => void
}

/**
 * 401/403 from the host (a `ServiceError`, by shape — this module stays
 * transport-free). Every seam above the log uses this same predicate, so
 * the board, the CLI and the MCP server cannot disagree about what a
 * refused credential is.
 */
export const isAuthRefusal = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status
  return status === 401 || status === 403
}

/**
 * A push refused because the last pull answered from the replica: a writer
 * must not decide what is new against a stale view (an importer would
 * resend ids the host already holds — platform#25). Sync, then retry.
 */
export class StaleReplicaError extends Error {
  constructor(public override readonly cause: unknown) {
    super(
      `the replica is stale (the host was unreachable: ${
        cause instanceof Error ? cause.message.slice(0, 80) : String(cause)
      }); sync and try again`
    )
    this.name = 'StaleReplicaError'
  }
}

/**
 * The host's log is not the one this replica copied (wiped, or restarted):
 * drop the replica and load from zero. Never absorbed as an outage.
 */
export class HostResetError extends Error {
  constructor(public readonly tail: number) {
    super(
      `the host's log no longer holds this replica's event at seq ${tail} (reset or restarted): the local copy must be rebuilt`
    )
    this.name = 'HostResetError'
  }
}

export class CachedLog implements SyncLog {
  readonly remote: SyncLog
  readonly cache: ReplicaLog
  private readonly onRemoteError: ((error: unknown) => void) | undefined
  /** How many times the host was asked for a delta; a cold load from a warm cache is 1. */
  remotePulls = 0
  /** The host was checked against the replica this session (`verifyHost`). */
  private verified = false

  /**
   * Is the host still the log this replica copied? Once per session that
   * starts from a warm replica (a reload, a new CLI run), before the first
   * delta: the host's event at the replica's tail must be the
   * replica's own. A host whose log was wiped (its counter runs on) no longer
   * has it; one restarted from zero has another event there, or none. Either
   * way `HostResetError` — the replica must go, and the caller reloads.
   * Nothing else notices: a delta only asks for what comes after the cursor
   * (the host-side epoch is asked for on tosijs-platform#14).
   */
  private async verifyHost(tail: number): Promise<void> {
    const [probe, held] = await Promise.all([
      this.remote.pull(tail - 1, 1),
      this.cache.pull(tail - 1, 1),
    ])
    const theirs = probe.events[0]
    const ours = held.events[0]
    if (!theirs || !ours || theirs.seq !== tail || theirs.id !== ours.id)
      throw new HostResetError(tail)
    this.verified = true
  }

  /** The last host failure a pull absorbed; null once a pull succeeds. */
  lastError: unknown = null

  constructor(options: CachedLogOptions) {
    this.remote = options.remote
    this.cache = options.cache
    this.onRemoteError = options.onRemoteError
  }

  /** True when the last pull answered from the replica because the host failed. */
  get stale(): boolean {
    return this.lastError !== null
  }

  async pull(after: number, limit = DEFAULT_PAGE): Promise<LogPage> {
    const local = await this.cache.pull(after, limit)
    if (local.events.length >= limit) {
      return { events: local.events, cursor: local.cursor, more: true }
    }
    // the cache is exhausted: fetch what the host has beyond it
    const tail = await this.cache.tail()
    const from = Math.max(after, tail, local.cursor)
    this.remotePulls++
    let remote: LogPage
    try {
      if (!this.verified && tail > 0) await this.verifyHost(tail)
      remote = await this.remote.pull(from, limit - local.events.length)
      // a replica this session built from the host is the host's copy
      this.verified = true
      this.lastError = null
    } catch (error) {
      // a refused credential is not an outage: the caller must act on it
      // (forget the token, ask for sign-in) — never serve the replica as if
      // the reader were still authorized. Nor is a host whose log is no
      // longer the one this replica copied: serving it would show deleted
      // work as current (board #16)
      if (isAuthRefusal(error) || error instanceof HostResetError) throw error
      // the host is down: serve what the replica holds, and say so
      if (tail === 0 && local.events.length === 0) throw error
      this.lastError = error
      this.onRemoteError?.(error)
      return { events: local.events, cursor: local.cursor, more: false }
    }
    if (remote.events.length) await this.cache.append(remote.events)
    const events = [...local.events, ...remote.events]
    const last = events[events.length - 1]
    return {
      events,
      cursor: last ? last.seq : after,
      // a remote that does not say `more` (a MemoryLog) means "a full page may have more"
      more: remote.more ?? remote.events.length >= limit - local.events.length,
    }
  }

  /**
   * To the host only; the replay that follows brings the events back through
   * the cache. Refused while the replica is stale — the one seam every
   * writer (CLI verb, MCP tool, board, importer) crosses, so the "never
   * write against a stale view" rule is not a per-command copy.
   */
  push(drafts: EventDraft[]): Promise<VirtaEvent[]> {
    if (this.stale) return Promise.reject(new StaleReplicaError(this.lastError))
    return this.remote.push(drafts)
  }
}
